import { useEffect, useState, type ReactNode } from 'react'
import { TransientSignInError, isAuthConfigured, signIn } from '../lib/auth'
import { setToken } from '../lib/api'
import { renewToken } from '../lib/session-token'
import { getConnectivity } from '../lib/connectivity'
import { restoreFeedbackLink, stashFeedbackLink } from '../userhub/deep-link'
import { restoreTourLink, stashTourLink } from '../tours/deepLink'

/**
 * Blocks the app until Microsoft Entra sign-in has produced an access token.
 *
 * When auth is NOT configured (no `auth` block in window.__NIOBE_CONFIG__ — i.e. local dev) this
 * renders its children immediately, so `npm start` keeps working against the stub identity with
 * no Entra round-trip. In prod the config is present and nothing renders until a token exists,
 * which guarantees no component can fire an unauthenticated request on first paint.
 */

/** Re-acquire well inside the ~60–90 min access-token lifetime. */
const REFRESH_INTERVAL_MS = 20 * 60 * 1000

/** Waiting for the network before sign-in can reach Entra: first retry, then doubling to the cap. */
const SIGNIN_RETRY_BASE_MS = 1000
const SIGNIN_RETRY_MAX_MS = 30_000

type State = 'checking' | 'ready' | 'redirecting' | 'error'

export function AuthGate({ children }: { children: ReactNode }): React.ReactElement | null {
  const configured = isAuthConfigured()
  const [state, setState] = useState<State>(configured ? 'checking' : 'ready')
  const [message, setMessage] = useState<string>('')
  // Sign-in could not reach Entra yet (Niobe Desktop launched at login before the network was up).
  const [waitingForNetwork, setWaitingForNetwork] = useState(false)

  useEffect(() => {
    if (!configured) return
    let cancelled = false
    let retry: number | undefined
    // A userhub link (?feedback=tickets&ticket=…, /feedback-triage?ticket=…) must survive a
    // loginRedirect round trip, and so must a guided tour's link (/tour/<n>, ?tour=<n>).
    stashFeedbackLink()
    stashTourLink()

    const attempt = async (delay: number): Promise<void> => {
      try {
        const result = await signIn()
        if (cancelled) return
        if (!result) {
          // A redirect is navigating away — keep the page inert rather than flashing the app.
          setState('redirecting')
          return
        }
        setToken(result.token)
        // Back in the URL before the children — and the feedback widget reading it — mount.
        restoreFeedbackLink()
        restoreTourLink()
        setState('ready')
      } catch (e) {
        if (cancelled) return
        if (e instanceof TransientSignInError) {
          // The network, not the session: wait and try again. No error screen, and no browser tab
          // that could not load anyway.
          setWaitingForNetwork(true)
          retry = window.setTimeout(() => void attempt(Math.min(delay * 2, SIGNIN_RETRY_MAX_MS)), delay)
          return
        }
        setMessage(e instanceof Error ? e.message : 'Sign-in failed')
        setState('error')
      }
    }
    void attempt(SIGNIN_RETRY_BASE_MS)

    return () => {
      cancelled = true
      if (retry !== undefined) window.clearTimeout(retry)
    }
  }, [configured])

  // Silent top-up so a long editing session does not die on an expired token.
  //
  // This is now a CONVENIENCE, not the mechanism. It is a `setInterval` in a renderer that may be
  // hidden in the tray and on a machine that sleeps, so it neither fires on time nor catches up —
  // relying on it is what let the token expire under a long idle. Every consumer resolves its own
  // token at the moment of use (lib/session-token.ts); this just keeps the common case warm.
  useEffect(() => {
    if (!configured || state !== 'ready') return
    const id = window.setInterval(() => {
      // Not while the backend is unreachable: the connectivity store renews the token itself, in
      // order, once the backend answers (lib/connectivity.ts).
      if (getConnectivity() !== 'online') return
      void renewToken().then((t) => {
        if (t) setToken(t)
        // A failed renewal is no longer silent. It is not fatal either — the next call renews
        // again, and a genuinely dead session surfaces as a refused handshake with a banner.
        else console.warn('[auth] periodic token renewal returned nothing — will retry on use')
      })
    }, REFRESH_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [configured, state])

  if (state === 'ready') return <>{children}</>

  if (state === 'error') {
    return (
      <div className="auth-gate" role="alert">
        <h1>Sign-in failed</h1>
        <p>{message}</p>
        <button type="button" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="auth-gate" role="status" aria-live="polite">
      <p>{waitingForNetwork ? 'Signing you in… waiting for the network.' : 'Signing you in…'}</p>
    </div>
  )
}
